import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('caseStatus');
const caseStatusProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicCaseStatusOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const caseStatuses = result.filters.find((f) => f.name === 'caseStatuses');
		if (!caseStatuses || caseStatuses.values.length === 0) {
			return [];
		}

		return caseStatuses.values.map((status) => ({
			value: status.name,
			trackingKey: status.name,
			queryParamValue: status.name,
			isDefault: false,
			label: status.label ?? status.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch {
		return [];
	}
};

const fetchCaseStatusOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	return fetchDynamicCaseStatusOptions(config);
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchCaseStatusOptions(config);
	const selected = new Set(queryParams);
	return allOptions.filter((option) => selected.has(option.queryParamValue));
};

export const filter: CreatedSelectFilter<'caseStatus'> = createMultiSelectFilter({
	id: 'caseStatus',
	requiresTopLevel3PDatasource: true,
	products: caseStatusProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'caseStatuses',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchCaseStatusOptions(config);
		},
	},
});
